/**
 * Settings → Automation: standing dispatch grants (docs/signing.md Appendix A).
 *
 * A grant lets the hub's router start, feed and stop agent runs for ready tasks on the chosen
 * machines and projects while you're away — within rate/concurrency/prompt limits and until it
 * expires. It is signed by this device's key and approved with ONE passkey tap that covers every
 * machine in it; each daemon verifies it offline against its pinned passkeys. It can never land,
 * deploy, use secrets or change devices. Revoking needs only this device's signature (no tap).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createDispatchGrantChallenge,
  createDispatchGrantStatement,
  createGrantRevocation,
  DEFAULT_GRANT_LIMITS,
  DEFAULT_GRANT_TTL_MS,
  type DispatchKind,
  isSessionEndorsement,
  signDispatchGrant,
  signGrantRevocation,
} from '@viko/crypto'
import { Button, Chip } from '@viko/transcript-ui'
import { Fingerprint, ShieldCheck, ShieldOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { clock } from '../lib/clock.js'
import { loadDevice } from '../lib/device-key.js'
import { passkeyErrorMessage, passkeys } from '../lib/passkeys.js'
import { qk, useGrants, useMachines, useProjects } from '../lib/queries.js'
import type { GrantRow } from '../lib/types.js'
import { errorText, useToast } from './ui.js'

const DAY = 86_400_000
const TTLS = [
  { label: '1 day', ms: DAY },
  { label: '7 days', ms: DEFAULT_GRANT_TTL_MS },
] as const

const KIND_LABEL: Record<DispatchKind, string> = {
  'run.start': 'Start runs for ready tasks',
  'run.send': 'Send follow-ups into a live run',
  'run.stop': 'Stop idle or stuck runs',
}

export interface GrantPrefill {
  machineIds?: string[]
  projectIds?: string[]
}

/** Build, sign (device key + one passkey tap) and upload a grant. */
export async function createGrant(
  api: ReturnType<typeof useBackend>['api'],
  input: {
    machineIds: string[]
    projectIds: string[]
    kinds: DispatchKind[]
    limits: typeof DEFAULT_GRANT_LIMITS
    ttlMs: number
  },
) {
  const dev = await loadDevice()
  if (!dev?.endorsement) throw new Error('Trust this device with a passkey first.')
  if (isSessionEndorsement(dev.endorsement.statement))
    throw new Error('Standing grants need a trusted device, not a session-only browser.')
  const statement = createDispatchGrantStatement({
    machineIds: input.machineIds,
    projectIds: input.projectIds,
    kinds: input.kinds,
    limits: input.limits,
    deviceKeyId: dev.deviceKeyId,
    now: clock.now(),
    ttlMs: input.ttlMs,
  })
  const assertion = await passkeys.assert(await createDispatchGrantChallenge(statement))
  const grant = await signDispatchGrant(
    statement,
    { deviceKeyId: dev.deviceKeyId, privateKey: dev.privateKey },
    assertion,
  )
  return api.addGrant(grant)
}

export async function revokeGrant(api: ReturnType<typeof useBackend>['api'], ids: string[]) {
  const dev = await loadDevice()
  if (!dev) throw new Error('This device has no signing key.')
  const statement = createGrantRevocation({
    grantIds: ids,
    deviceKeyId: dev.deviceKeyId,
    now: clock.now(),
  })
  return api.revokeGrants(
    await signGrantRevocation(statement, {
      deviceKeyId: dev.deviceKeyId,
      privateKey: dev.privateKey,
    }),
  )
}

export function Automation({ prefill }: { prefill?: GrantPrefill }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const automation = useQuery({ queryKey: ['automation'], queryFn: () => b.api.automation() })
  const setEnabled = async (enabled: boolean) => {
    try {
      await b.api.setAutomation(enabled)
      if (!enabled) toast('Automation off: all grants revoked')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['automation'] }),
        qc.invalidateQueries({ queryKey: qk.grants }),
      ])
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const grants = useGrants()
  const [creating, setCreating] = useState(
    !!(prefill?.machineIds?.length || prefill?.projectIds?.length),
  )
  const list = grants.data ?? []
  const active = list.filter((g) => g.active)
  const past = list.filter((g) => !g.active)
  return (
    <div className="grid gap-3 px-4">
      <p className="m-0 text-[14px] leading-5 text-text-2">
        A <strong>grant</strong> lets Viko start, feed and stop agent runs for ready tasks on its
        own — on the machines and projects you pick, within hourly and concurrency limits, until it
        expires. It never lands to main, ships, uses secrets or changes devices; those still ask
        you. One passkey tap covers every machine; each machine checks it against your passkeys.
      </p>
      <p role="note" className="m-0 rounded-xl bg-surface-2 p-3 text-[14px] leading-5 text-text-2">
        Automation is off by default. Enabling grants lets the hosted service choose agent prompts
        on your machines. A compromised service could use that authority to read or change files.
        Grants expire within 7 days. Disabling revokes all grants immediately; already running
        agents must be stopped separately.
      </p>
      <label className="flex items-center gap-2 text-[14px]">
        <input
          type="checkbox"
          disabled={!automation.isSuccess}
          checked={automation.data?.enabled === true}
          onChange={(e) => void setEnabled(e.target.checked)}
        />
        Enable standing grants
      </label>
      <div>
        <Button size="sm" onClick={() => void setEnabled(false)}>
          Disable automation and revoke all grants
        </Button>
      </div>
      {active.length ? (
        <ul className="m-0 grid list-none gap-2 p-0" aria-label="Active grants">
          {active.map((g) => (
            <GrantItem key={g.id} g={g} />
          ))}
        </ul>
      ) : grants.isSuccess ? (
        <p className="m-0 rounded-xl bg-surface-2 p-3 text-[14px] text-text-2">
          No active grant: ready tasks wait for a tap in the Inbox.
        </p>
      ) : null}
      {automation.data?.enabled && creating ? (
        <GrantForm prefill={prefill} onDone={() => setCreating(false)} />
      ) : (
        <div>
          <Button
            variant="primary"
            size="sm"
            icon={<ShieldCheck size={14} />}
            disabled={!automation.data?.enabled}
            onClick={() => setCreating(true)}
          >
            New grant
          </Button>
        </div>
      )}
      {past.length ? (
        <details>
          <summary className="cursor-pointer text-[14px] text-text-2">
            {past.length} expired or revoked
          </summary>
          <ul className="m-0 mt-2 grid list-none gap-2 p-0">
            {past.map((g) => (
              <GrantItem key={g.id} g={g} />
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

function GrantItem({ g }: { g: GrantRow }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const projects = useProjects()
  const machines = useMachines()
  const [busy, setBusy] = useState(false)
  const pn = (id: string) => projects.data?.find((p) => p.id === id)?.name ?? id
  const mn = (id: string) => machines.data?.find((m) => m.id === id)?.name ?? id
  const revoke = async () => {
    setBusy(true)
    try {
      await revokeGrant(b.api, [g.id])
      await qc.invalidateQueries({ queryKey: qk.grants })
      toast('Grant revoked')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <li
      className="card grid gap-1.5 p-3"
      aria-label={`Grant for ${g.projectIds.map(pn).join(', ')}`}
    >
      <div className="flex items-start gap-2">
        <span className="grid min-w-0 flex-1 gap-0.5">
          <span className="text-[15px] font-medium">{g.projectIds.map(pn).join(', ')}</span>
          <span className="text-[14px] text-text-3">on {g.machineIds.map(mn).join(', ')}</span>
        </span>
        {g.active ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<ShieldOff size={14} />}
            disabled={busy}
            onClick={() => void revoke()}
          >
            Revoke
          </Button>
        ) : (
          <Chip>{g.revokedAt ? 'revoked' : 'expired'}</Chip>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 text-[14px] text-text-3">
        {g.kinds.map((k) => (
          <Chip key={k}>{k}</Chip>
        ))}
        <span>
          ≤{g.limits.maxStartsPerHour} starts/h · ≤{g.limits.maxConcurrentRuns} at once ·{' '}
          {g.active
            ? `expires ${new Date(g.expiresAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`
            : ''}
        </span>
      </div>
    </li>
  )
}

function GrantForm({ prefill, onDone }: { prefill?: GrantPrefill; onDone: () => void }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const projects = useProjects()
  const machines = useMachines()
  const [projectIds, setProjectIds] = useState<string[]>(prefill?.projectIds ?? [])
  const [machineIds, setMachineIds] = useState<string[]>(prefill?.machineIds ?? [])
  const [kinds, setKinds] = useState<DispatchKind[]>(['run.send', 'run.start', 'run.stop'])
  const [limits, setLimits] = useState({ ...DEFAULT_GRANT_LIMITS })
  const [ttlMs, setTtl] = useState<number>(DEFAULT_GRANT_TTL_MS)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!machineIds.length && machines.data?.length && !prefill?.machineIds?.length)
      setMachineIds(machines.data.map((m) => m.id))
  }, [machines.data, machineIds.length, prefill?.machineIds])
  const toggle = <T,>(list: T[], v: T, on: boolean) =>
    on ? [...new Set([...list, v])] : list.filter((x) => x !== v)
  const ok = projectIds.length > 0 && machineIds.length > 0 && kinds.length > 0
  const submit = async () => {
    setBusy(true)
    try {
      await createGrant(b.api, { machineIds, projectIds, kinds, limits, ttlMs })
      await qc.invalidateQueries({ queryKey: qk.grants })
      toast('Grant active — Viko can start ready tasks on its own')
      onDone()
    } catch (e) {
      const m = (e as { name?: string }).name ? passkeyErrorMessage(e) : errorText(e)
      toast(m, 'error')
    } finally {
      setBusy(false)
    }
  }
  const num = (k: keyof typeof limits, label: string, min: number, max: number) => (
    <label className="field">
      <span>{label}</span>
      <input
        className="input"
        type="number"
        min={min}
        max={max}
        value={limits[k]}
        onChange={(e) =>
          setLimits((l) => ({
            ...l,
            [k]: Math.max(min, Math.min(max, Number(e.target.value) || min)),
          }))
        }
      />
    </label>
  )
  return (
    <form
      className="card grid gap-4 p-4"
      aria-label="New grant"
      onSubmit={(e) => {
        e.preventDefault()
        if (ok) void submit()
      }}
    >
      <fieldset className="m-0 grid gap-1 border-0 p-0">
        <legend className="pb-1 text-[14px] font-semibold">Projects</legend>
        {(projects.data ?? []).map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-[15px]">
            <input
              type="checkbox"
              checked={projectIds.includes(p.id)}
              onChange={(e) => setProjectIds((l) => toggle(l, p.id, e.target.checked))}
            />
            {p.name}
          </label>
        ))}
        {projects.isSuccess && !projects.data.length ? (
          <span className="text-[14px] text-text-3">No projects yet.</span>
        ) : null}
      </fieldset>
      <fieldset className="m-0 grid gap-1 border-0 p-0">
        <legend className="pb-1 text-[14px] font-semibold">Machines</legend>
        {(machines.data ?? []).map((m) => (
          <label key={m.id} className="flex items-center gap-2 text-[15px]">
            <input
              type="checkbox"
              checked={machineIds.includes(m.id)}
              onChange={(e) => setMachineIds((l) => toggle(l, m.id, e.target.checked))}
            />
            {m.name}
          </label>
        ))}
      </fieldset>
      <fieldset className="m-0 grid gap-1 border-0 p-0">
        <legend className="pb-1 text-[14px] font-semibold">Allowed</legend>
        {(Object.keys(KIND_LABEL) as DispatchKind[]).map((k) => (
          <label key={k} className="flex items-center gap-2 text-[15px]">
            <input
              type="checkbox"
              checked={kinds.includes(k)}
              onChange={(e) => setKinds((l) => toggle(l, k, e.target.checked))}
            />
            {KIND_LABEL[k]}
          </label>
        ))}
      </fieldset>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        {num('maxStartsPerHour', 'Starts per hour (per machine)', 1, 120)}
        {num('maxConcurrentRuns', 'Runs at once (per machine)', 1, 32)}
        {num('maxPromptBytes', 'Max prompt size (bytes)', 1000, 200_000)}
      </div>
      <div className="field">
        <span id="grant-ttl">Expires after</span>
        <div className="choice" role="radiogroup" aria-labelledby="grant-ttl">
          {TTLS.map((t) => (
            <button
              key={t.ms}
              type="button"
              role="radio"
              aria-checked={ttlMs === t.ms}
              onClick={() => setTtl(t.ms)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-1 text-[14px] text-text-3">
          Signed by this device, approved with one passkey tap.
        </span>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="submit"
          icon={<Fingerprint size={14} />}
          disabled={!ok || busy}
        >
          {busy ? 'Waiting for passkey…' : 'Allow with passkey'}
        </Button>
      </div>
    </form>
  )
}
